/* Online openers — compact layout. Presence-triggered 1:1 openers (ADR 0002):
   when a fan comes online, send one opener, unsend it if unanswered after the
   send period, or keep it and hand the chat to the assistant if they reply.
   Four blocks: the Openers switches, Who gets one (include/exclude lists with
   the Setup tick-and-add dropdown), Timing, and Messages (recommended openers,
   or your own pool). Campaign settings save on change; each opener saves on
   its own when you leave its field. Config only — the send loop runs in
   bigdayz.mass_messages. */

import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../api/client';
import type { OfList, OnlineMessageCampaign, OnlineMessageTemplate } from '../../../api/client';
import { Btn, Card, ErrorLine, Icon, Loading, PageFrame, RefreshBtn, Row, SaveStatus, Stack, TextInput, Toggle, Warn } from '../../ChatAssistant/compact/kit';
import { usePolicy, patchNow } from '../../ChatAssistant/compact/usePolicy';
import { AddLists, Chip, ChipRow, Stepper } from '../../ChatAssistant/compact/setup/parts';
import { font as f } from '../../ChatAssistant/compact/style';
import { C } from '../../ChatAssistant/compact/tokens';
import { IconBtn } from './NewSubsPage';

export const MIN_SEND_PERIOD_MIN = 1;
export const MAX_SEND_PERIOD_MIN = 1440;
export const MAX_COOLDOWN_MIN = 43200;
// Must not be an OnlyFans reserved name: "online" collides with OF's built-in
// Online filter and 400s at creation. Kept in sync with the backend default.
export const DEFAULT_ONLINE_ROSTER_LIST = 'online_seen';
const BUILTIN_LISTS = ['fans', 'following'];

type Campaign = Pick<OnlineMessageCampaign, 'is_enabled' | 'message_source' | 'send_period_seconds' | 'cooldown_seconds' | 'max_sends_per_iteration' | 'include_lists' | 'exclude_lists' | 'add_online_to_list' | 'online_list_name'>;

const FALLBACK: Campaign = {
  is_enabled: false, message_source: 'auto', send_period_seconds: 1800, cooldown_seconds: 7200, max_sends_per_iteration: 20,
  include_lists: [], exclude_lists: [], add_online_to_list: false, online_list_name: DEFAULT_ONLINE_ROSTER_LIST,
};

const pick = (c: OnlineMessageCampaign): Campaign => ({
  is_enabled: c.is_enabled, message_source: c.message_source || 'auto', send_period_seconds: c.send_period_seconds, cooldown_seconds: c.cooldown_seconds,
  max_sends_per_iteration: c.max_sends_per_iteration, include_lists: c.include_lists || [], exclude_lists: c.exclude_lists || [],
  add_online_to_list: c.add_online_to_list, online_list_name: c.online_list_name || DEFAULT_ONLINE_ROSTER_LIST,
});

/** A row in "your own openers": `id` is null until the opener is created. */
interface Opener { key: string; id: number | null; text: string; on: boolean; tpl?: OnlineMessageTemplate }
let seq = 0;
const openerOf = (tpl: OnlineMessageTemplate): Opener => ({ key: `o-${tpl.id}`, id: tpl.id, text: tpl.text, on: tpl.is_enabled, tpl });

export function OnlinePage({ accountId }: { accountId: string }) {
  const { t } = useTranslation('assistantCompact');
  const [openers, setOpeners] = useState<Opener[]>([]);
  const [ofLists, setOfLists] = useState<OfList[]>([]);
  const [opErr, setOpErr] = useState<string | null>(null);
  const enabledOpeners = openers.filter((o) => o.id !== null && o.on).length;
  // Why the campaign can't be on yet (the backend 422s the same cases). The switch
  // is locked while off and blocked: a held-back save looked on but was never sent.
  const enableBlocker = (c: Campaign) => (!c.include_lists.length ? t('am.onl.needInclude') : c.message_source === 'custom' && !enabledOpeners ? t('am.onl.needOpener') : null);

  const policy = usePolicy<Campaign>(
    async () => {
      const cfg = await api.getOnlineMessages(accountId);
      setOpeners(cfg.templates.map(openerOf));
      return pick(cfg.campaign);
    },
    async (b) => pick(await api.updateOnlineMessagesCampaign(accountId, { ...b, online_list_name: b.online_list_name.trim() || DEFAULT_ONLINE_ROSTER_LIST })),
    FALLBACK,
    accountId,
    600,
    (b) => (b.is_enabled ? enableBlocker(b) : null),
  );
  useEffect(() => { api.getAccountOfLists(accountId).then(setOfLists, () => {}); }, [accountId]);
  const refreshLists = async () => { await api.refreshListsAndWait(accountId); setOfLists(await api.getAccountOfLists(accountId)); };
  // A held-back save (e.g. "add an opener first") goes through once openers change.
  const flush = policy.auto.flush;
  useEffect(() => { flush(); }, [enabledOpeners, flush]);

  const b = policy.body;
  const blocker = enableBlocker(b);
  const set = (p: Partial<Campaign>) => policy.patch(p);
  const setNow = (p: Partial<Campaign>) => patchNow(policy, p);
  const period = Math.round(b.send_period_seconds / 60);
  const cooldown = Math.round(b.cooldown_seconds / 60);
  const used = new Set([...b.include_lists, ...b.exclude_lists]);
  const pool = Array.from(new Set([...BUILTIN_LISTS, ...ofLists.map((l) => l.of_list_name).filter(Boolean)])).filter((n) => !used.has(n)).map((n) => ({ id: n, label: n }));

  // Openers save straight to their own endpoints.
  const fail = (e: unknown) => setOpErr(e instanceof Error ? e.message : String(e));
  const editOpener = (key: string, p: Partial<Opener>) => setOpeners((a) => a.map((o) => (o.key === key ? { ...o, ...p } : o)));
  const commitOpener = async (o: Opener) => {
    const text = o.text.trim();
    try {
      setOpErr(null);
      if (o.id === null) {
        if (!text) return;
        editOpener(o.key, openerOf(await api.createOnlineMessageTemplate(accountId, { text })));
      } else if (text && text !== o.tpl?.text) {
        editOpener(o.key, openerOf(await api.updateOnlineMessageTemplate(accountId, o.id, { text })));
      }
    } catch (e) { fail(e); }
  };
  const toggleOpener = async (o: Opener) => {
    if (o.id === null) return;
    try { setOpErr(null); editOpener(o.key, openerOf(await api.updateOnlineMessageTemplate(accountId, o.id, { is_enabled: !o.on }))); } catch (e) { fail(e); }
  };
  const removeOpener = async (o: Opener) => {
    try {
      setOpErr(null);
      if (o.id !== null) await api.deleteOnlineMessageTemplate(accountId, o.id);
      setOpeners((a) => a.filter((x) => x.key !== o.key));
    } catch (e) { fail(e); }
  };

  const frame = (children: ReactNode, right?: ReactNode) => <PageFrame headline={t('am.onl.title')} desc={t('am.onl.desc')} right={right}>{children}</PageFrame>;
  if (policy.state === 'loading') return frame(<Loading />);
  if (policy.state === 'failed') return frame(<ErrorLine>{t('loadFailed')}</ErrorLine>);

  const dim = { opacity: b.is_enabled ? 1 : 0.45, transition: 'opacity .15s' };
  return frame(
    <Stack gap={18}>
      <Card clip={false}>
        <Row label={t('am.onl.enable')} help={blocker ?? (b.is_enabled ? t('am.onl.enableOn') : t('am.onl.enableOff'))} helpColor={blocker ? C.purplePale : undefined}>
          <Toggle on={b.is_enabled} disabled={!b.is_enabled && !!blocker} title={blocker ?? undefined} onChange={(v) => setNow({ is_enabled: v })} />
        </Row>
        <Row label={t('am.onl.addToList')} help={b.add_online_to_list ? t('am.onl.addToListOn') : t('am.onl.addToListOff')}>
          {b.add_online_to_list && <TextInput value={b.online_list_name} onChange={(v) => set({ online_list_name: v })} placeholder={DEFAULT_ONLINE_ROSTER_LIST} width={180} title={t('am.onl.listName')} />}
          <Toggle on={b.add_online_to_list} onChange={(v) => setNow({ add_online_to_list: v })} />
        </Row>
      </Card>

      <Block title={t('am.onl.who')} hint={t('am.onl.whoHint')} style={dim} right={<RefreshBtn onRefresh={refreshLists} title={t('su.refreshLists')} />}>
        <Card clip={false}>
          <div>
            <Row label={<Counted n={b.include_lists.length}>{t('am.onl.include')}</Counted>} help={t('am.onl.includeHelp')} />
            <ChipRow>
              {b.include_lists.map((l) => <Chip key={l} label={l} accent onRemove={() => setNow({ include_lists: b.include_lists.filter((x) => x !== l) })} />)}
              <AddLists pool={pool} onAdd={(a) => setNow({ include_lists: [...b.include_lists, ...a] })} />
            </ChipRow>
            {!b.include_lists.length && <div style={{ padding: '0 14px 10px' }}><Warn>{t('am.onl.includeWarn')}</Warn></div>}
          </div>
          <div>
            <Row label={<Counted n={b.exclude_lists.length}>{t('am.onl.exclude')}</Counted>} help={t('am.onl.excludeHelp')} />
            <ChipRow>
              {b.exclude_lists.map((l) => <Chip key={l} label={l} onRemove={() => setNow({ exclude_lists: b.exclude_lists.filter((x) => x !== l) })} />)}
              <AddLists pool={pool} onAdd={(a) => setNow({ exclude_lists: [...b.exclude_lists, ...a] })} />
            </ChipRow>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', font: f(400, 11.5), color: C.muted }}>
            <Icon name="info" size={12} />{t('am.onl.skipped')}
          </div>
        </Card>
      </Block>

      <Block title={t('am.onl.timing')} hint={t('am.onl.timingHint')} style={dim}>
        <Card>
          <Row label={t('am.onl.period')} help={t('am.onl.periodHelp')}>
            <Stepper value={period} unit={t('am.unit.min')}
              onDec={() => set({ send_period_seconds: Math.max(MIN_SEND_PERIOD_MIN, period - 5) * 60 })}
              onInc={() => set({ send_period_seconds: Math.min(MAX_SEND_PERIOD_MIN, period + 5) * 60 })} />
          </Row>
          <Row label={t('am.onl.cooldown')} help={t('am.onl.cooldownHelp')}>
            <Stepper value={cooldown} unit={t('am.unit.min')}
              onDec={() => set({ cooldown_seconds: Math.max(0, cooldown - 15) * 60 })}
              onInc={() => set({ cooldown_seconds: Math.min(MAX_COOLDOWN_MIN, cooldown + 15) * 60 })} />
          </Row>
        </Card>
      </Block>

      <Block title={t('am.onl.messages')} hint={t('am.onl.messagesHint')} style={dim}>
        <Card>
          <Row label={t('am.onl.recommended')} help={b.message_source === 'auto' ? t('am.onl.recommendedOn') : t('am.onl.recommendedOff')}>
            <Toggle on={b.message_source === 'auto'} onChange={(v) => setNow({ message_source: v ? 'auto' : 'custom' })} />
          </Row>
          {b.message_source === 'custom' && openers.map((o, k) => (
            <div key={o.key} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px 6px 34px' }}>
              <span style={{ width: 18, flex: 'none', font: f(600, 11), color: C.muted, textAlign: 'right' }}>{k + 1}</span>
              <input
                autoFocus={o.id === null && !o.text}
                value={o.text}
                onChange={(e) => editOpener(o.key, { text: e.target.value })}
                onBlur={() => void commitOpener(o)}
                onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                placeholder={t('am.onl.openerPh')}
                style={{ height: 28, flex: 1, minWidth: 0, boxSizing: 'border-box', background: C.input, border: `1px solid ${C.border}`, borderRadius: 7, padding: '0 10px', color: o.on || o.id === null ? C.text : C.muted, font: f(500, 12.5), outline: 'none' }}
              />
              {o.tpl && <span title={t('am.onl.statsTip')} style={{ flex: 'none', font: f(400, 11), color: C.muted, whiteSpace: 'nowrap' }}>{t('am.onl.stats', { sent: o.tpl.sent_count, replied: o.tpl.converted_count })}</span>}
              <Toggle on={o.on} disabled={o.id === null} onChange={() => void toggleOpener(o)} title={o.on ? t('am.onl.openerOn') : t('am.onl.openerOff')} />
              <IconBtn icon="trash-2" title={t('am.onl.deleteOpener')} onClick={() => void removeOpener(o)} />
            </div>
          ))}
          {b.message_source === 'custom' && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px 10px 34px', flexWrap: 'wrap' }}>
              <Btn kind="text" icon="plus" onClick={() => setOpeners((a) => [...a, { key: `o-new-${(seq += 1)}`, id: null, text: '', on: true }])} style={{ color: C.purpleLt }}>{t('am.onl.addOpener')}</Btn>
              <span style={{ font: f(400, 11.5), color: C.muted }}>{t('am.onl.random')}</span>
              <span style={{ flex: 1 }} />
              {!enabledOpeners && <Warn>{t('am.onl.openerWarn')}</Warn>}
            </div>
          )}
        </Card>
        {opErr && <ErrorLine>{opErr}</ErrorLine>}
      </Block>
    </Stack>,
    <SaveStatus status={policy.auto.status} error={policy.auto.error} onRetry={policy.auto.retry} idleLabel />,
  );
}

function Block({ title, hint, style, right, children }: { title: string; hint: string; style?: React.CSSProperties; right?: ReactNode; children: ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, ...style }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ font: f(600, 13), color: C.text }}>{title}</span>
        <span style={{ font: f(400, 11.5), color: C.muted }}>{hint}</span>
        {right && <span style={{ marginLeft: 'auto', alignSelf: 'center' }}>{right}</span>}
      </div>
      {children}
    </div>
  );
}

function Counted({ n, children }: { n: number; children: ReactNode }) {
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      {children}
      <span style={{ font: f(600, 10.5), color: C.purplePale, background: C.tint16, padding: '1px 6px', borderRadius: 99 }}>{n}</span>
    </span>
  );
}
